import {ChangeDetectorRef, Component, Input, OnInit} from "@angular/core";
import LOADING_STATES from "./new.corporate.loading.states";
import {TranslateManager} from "../../../../../../../../../common/translate";
import {FormBuilder} from "@angular/forms";
import {
    CountriesService,
    CurrenciesService,
    TotalBalancesService
} from "../../../../../../../../../common/api/dictionaries";
import {Country, DictionaryObject, Money} from "../../../../../../../../../common/api";
import {Currency} from "../../../../../../../shared/api/entity";
import {includes} from 'lodash';
import {CorporateAccountsService} from "../../../../../shared/api/service";
import {
    AccountCurrencies,
    Address,
    AuthorisedSignatory,
    BusinessCustomerInformation,
    ContactInformation,
    CorporateAccount,
    Directors,
    ExpectedTurnover,
    InitialDeposits,
    Introducer,
    RegisteredOfficeAddress,
    Shareholders,
    StatementAffairs,
    UsageReferral
} from "../../../../../shared/api/entity";
import {isNullOrUndefined} from "util";
import {LoadingStateHolder} from '../../../../../../../../../common/providers/loading/loading.service';
import {isValidNumber, parse} from "libphonenumber-js";

export const DATE_FORMAT = "DD.MM.YYYY";

@Component({
    selector: 'tfl-new-corporate-profile-detail-details',
    template: require('./new.corporate.details.template.html')
})
export class NewCorporateDetailsComponent implements OnInit {

    static readonly EMAIL_REGEXP = /\S{1,}@\S{2,}\.\S{2,}$/;
    static readonly URL_REGEXP = /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)/;

    @Input("profile-id")
    public profileId: number;
    systemOrWL: string;
    loadingState: LoadingStateHolder;

    countries: Array<Country> = [];
    initialDeposit: DictionaryObject[] = [];
    currencies: Currency[] = [];
    availableCurrencyIds: number[] = [];

    businessCustomerInformation: BusinessCustomerInformation = null;
    registeredOfficeAddress: RegisteredOfficeAddress = null;
    officialBusinessLocation: Address = null;
    contactInformation: ContactInformation = null;
    statementAffairs: StatementAffairs = null;
    initialDeposits: InitialDeposits = null;
    expectedTurnover: ExpectedTurnover = null;
    authorisedSignatory: AuthorisedSignatory = null;
    directors: Directors = null;
    shareholders: Shareholders = null;
    introducer: Introducer = null;
    accountCurrencies: AccountCurrencies = null;
    usageReferral: UsageReferral = null;

    currenciesLoaded: boolean = false;
    availableCurrenciesLoaded: boolean = false;

    constructor(private _fb: FormBuilder,
                private _cdr: ChangeDetectorRef,
                private _translate: TranslateManager,
                private _countriesService: CountriesService,
                private _currenciesService: CurrenciesService,
                private _totalBalancesService: TotalBalancesService,
                private _corporateAccountsService: CorporateAccountsService) {

        this.loadingState = new LoadingStateHolder([
            LOADING_STATES.COUNTRIES,
            LOADING_STATES.INITIAL_DEPOSIT,
            LOADING_STATES.CORPORATE_ACCOUNT,
            LOADING_STATES.CURRENCIES,
            LOADING_STATES.AVAILABLE_CURRENCIES
        ], _cdr);
    }

    ngOnInit() {
        this.loadCorporateProfile();
        this.loadCurrencies();
        this.loadAvailableCurrencies();
        this.loadCountries();
        this.loadInitialDeposit();
    }

    getSystemOrWLName(): string {
        return isNullOrUndefined(this.systemOrWL) ? '' : this.systemOrWL;
    }

    private loadCurrencies(): void {
        this._currenciesService.getAppreciateCurrencies().subscribe(currencies => {
            this.currencies = currencies;
            this.loadingState.done(LOADING_STATES.CURRENCIES);
            this.currenciesLoaded = true;
            this.updateCurrencies();
        });
    }

    private loadAvailableCurrencies(): void {
        this._currenciesService.getAvailableCurrencies().subscribe(availableCurrencies => {
            this.availableCurrencyIds = availableCurrencies.map(({id}) => id);
            this.loadingState.done(LOADING_STATES.AVAILABLE_CURRENCIES);
            this.availableCurrenciesLoaded = true;
            this.updateCurrencies();
        });
    }

    private updateCurrencies(): void {
        if (this.currenciesLoaded && this.availableCurrenciesLoaded) {
            this.currencies = this.currencies.map(currency => {
                currency.available = includes(this.availableCurrencyIds, currency.id);
                return currency
            })
        }
    }

    private loadCorporateProfile() {
        this._corporateAccountsService.getCorporateAccount(this.profileId).subscribe(corporateAccount => {
            this.businessCustomerInformation = NewCorporateDetailsComponent.mapToBusinessCustomerInformation(corporateAccount);
            this.registeredOfficeAddress = NewCorporateDetailsComponent.mapToRegisteredOfficeAddress(corporateAccount);
            this.officialBusinessLocation = NewCorporateDetailsComponent.mapToOfficialBusinessLocation(corporateAccount);
            this.contactInformation = NewCorporateDetailsComponent.mapToContactInformation(corporateAccount);
            this.statementAffairs = NewCorporateDetailsComponent.mapToStatementAffairs(corporateAccount);
            this.initialDeposits = NewCorporateDetailsComponent.mapToInitialDeposits(corporateAccount);
            this.expectedTurnover = NewCorporateDetailsComponent.mapToExpectedTurnover(corporateAccount);
            this.authorisedSignatory = NewCorporateDetailsComponent.mapToAuthorisedSignatory(corporateAccount);
            this.directors = corporateAccount.directors;
            this.shareholders = corporateAccount.shareholders;
            this.introducer = NewCorporateDetailsComponent.mapToIntroducer(corporateAccount);
            this.accountCurrencies = NewCorporateDetailsComponent.mapToAccountCurrencies(corporateAccount);
            this.usageReferral = NewCorporateDetailsComponent.mapToUsageReferral(corporateAccount);
            this.systemOrWL = corporateAccount.systemOrWL;

            this.loadingState.done(LOADING_STATES.CORPORATE_ACCOUNT);
        });
    }

    private loadCountries() {
        this._countriesService.getCountries().subscribe(countries => {
            this.countries = countries;
            this.loadingState.done(LOADING_STATES.COUNTRIES);
        });
    }

    private loadInitialDeposit() {
        this._totalBalancesService.getTotalBalances()
            .subscribe(totalBalances => {
                this.initialDeposit = totalBalances;
                this.loadingState.done(LOADING_STATES.INITIAL_DEPOSIT);
            });
    }

    private static mapToBusinessCustomerInformation(corporateAccount: CorporateAccount): BusinessCustomerInformation {
        return {
            entityName: corporateAccount.entityName,
            registrationDate: corporateAccount.registrationDate,
            tradingName: isNullOrUndefined(corporateAccount.tradingName) ? null : corporateAccount.tradingName,
            registrationNumber: corporateAccount.registrationNumber,
            taxIdentificationNumber: isNullOrUndefined(corporateAccount.taxIdentificationNumber) ? null : corporateAccount.taxIdentificationNumber,
            userEmail: corporateAccount.userEmail,
            businessCategories: isNullOrUndefined(corporateAccount) ? [] : corporateAccount.businessCategories,
            legalDocuments: corporateAccount.legalDocuments,
            profileStatus: corporateAccount.profileStatus,
            companySignatoryPhone: corporateAccount.authorisedSignatoryPhone,
            ipAddress: corporateAccount.ipAddress,
            ipCountry: corporateAccount.ipCountry
        }
    }

    private static mapToRegisteredOfficeAddress(corporateAccount: CorporateAccount): RegisteredOfficeAddress {
        return {
            address: corporateAccount.companyAddress,
            city: corporateAccount.companyCity,
            countyOrRegion: corporateAccount.companyCountyOrRegion,
            zipCode: corporateAccount.companyZipCode,
            country: corporateAccount.companyCountry
        }
    }

    private static mapToOfficialBusinessLocation(corporateAccount: CorporateAccount): Address {
        let businessLocation = corporateAccount.businessLocation;
        if (isNullOrUndefined(businessLocation)) {
            return {
                address: '',
                city: '',
                countyOrRegion: '',
                zipCode: '',
                country: null,
                attachmentItems: isNullOrUndefined(corporateAccount.businessAddress) ? [] : corporateAccount.businessAddress
            }
        }
        return {
            address: businessLocation.address,
            city: businessLocation.city,
            countyOrRegion: businessLocation.countyOrRegion ? businessLocation.countyOrRegion : '',
            zipCode: businessLocation.zipCode,
            country: businessLocation.country,
            attachmentItems: isNullOrUndefined(corporateAccount.businessAddress) ? [] : corporateAccount.businessAddress
        };
    }

    private static mapToContactInformation(corporateAccount: CorporateAccount): ContactInformation {
        return {
            phone: corporateAccount.phone,
            website: corporateAccount.website,
            email: corporateAccount.email,
            rejectedEmail: corporateAccount.rejectedEmail
        }
    }

    private static mapToStatementAffairs(corporateAccount: CorporateAccount): StatementAffairs {

        if (corporateAccount && corporateAccount.affairsSpecificCountries) {
            return {
                affairsSpecificCountries: corporateAccount.affairsSpecificCountries.map((id) => String(id)),
                affairsBusinessAndPurpose: corporateAccount.affairsBusinessAndPurpose
            }
        } else {
            return {
                affairsSpecificCountries: [],
                affairsBusinessAndPurpose: corporateAccount.affairsBusinessAndPurpose
            }
        }
    }

    private static mapToInitialDeposits(corporateAccount: CorporateAccount): InitialDeposits {
        return {
            sourceOfInitialDeposit: corporateAccount.affairsSourceOfInitialDeposit,
            bankJurisdiction: corporateAccount.bankJurisdiction,
            sourcesOfFunds: corporateAccount.businessSourceOfFundsOther,
            initialDeposit: corporateAccount.initialDeposit,
            accountName: corporateAccount.accountName
        }
    }

    private static mapToExpectedTurnover(corporateAccount: CorporateAccount): ExpectedTurnover {
        return {
            averageIncomingTransferAmount: corporateAccount.averageIncomingTransferAmount,
            plannedIncomingTurnoverAmount: corporateAccount.plannedIncomingTurnoverAmount,
            monthlyIncomingTransfersCount: corporateAccount.monthlyIncomingTransfersCount,
            averageOutgoingTransferAmount: corporateAccount.averageOutgoingTransferAmount,
            plannedOutgoingTurnoverAmount: corporateAccount.plannedOutgoingTurnoverAmount,
            monthlyOutgoingTransfersCount: corporateAccount.monthlyOutgoingTransfersCount,
            mainSuppliers: corporateAccount.affairsMainSuppliers,
            mainClients: corporateAccount.affairsMainClients
        }
    }

    private static mapToAuthorisedSignatory(corporateAccount: CorporateAccount): AuthorisedSignatory {
        return {
            firstName: corporateAccount.authorisedSignatoryFirstName,
            lastName: corporateAccount.authorisedSignatoryLastName,
            passportNo: corporateAccount.authorisedSignatoryPassportNo,
            nationality: corporateAccount.authorisedSignatoryNationality,
            secondNationality: corporateAccount.authorisedSignatorySecondNationality,
            dateOfBirth: corporateAccount.authorisedSignatoryDateOfBirth,
            attorneyPowerExpireDate: corporateAccount.authorisedSignatoryAttorneyPowerExpireDate,
            dateIssue: corporateAccount.authorisedSignatoryPassportIssueDate,
            dateExpiry: corporateAccount.authorisedSignatoryPassportExpiryDate,

            address: NewCorporateDetailsComponent.mapToAuthorisedSignatoryAddress(corporateAccount),

            jobTitle: corporateAccount.authorisedSignatoryJobTitle,
            phone: corporateAccount.authorisedSignatoryPhone,
            email: corporateAccount.authorisedSignatoryEmail,
            linkedin: corporateAccount.authorisedSignatoryLinkedin,
            facebook: corporateAccount.authorisedSignatoryFacebook,

            passportAttachments: corporateAccount.authorisedSignatoryPassportAttachments,
            selfieAttachments: corporateAccount.authorisedSignatorySelfieAttachments,
            addressAttachments: corporateAccount.authorisedSignatoryAddressAttachments,
        }
    }

    private static mapToIntroducer(corporateAccount: CorporateAccount): Introducer {
        return {
            introducerName: corporateAccount.introducerName,
            introducerEmail: corporateAccount.introducerEmail,
        }
    }

    private static mapToAccountCurrencies(corporateAccount: CorporateAccount): AccountCurrencies {
        return {
            masterAccountCurrency: corporateAccount.masterAccountCurrency,
            additionalCurrencies: corporateAccount.requiredCurrencies.map((id) => String(id)),
        }
    }

    private static mapToUsageReferral(corporateAccount: CorporateAccount): UsageReferral {
        return {
            productsCustom: corporateAccount.productsCustom,
            reasonsCustom: corporateAccount.reasonsCustom,
            sourcesCustom: corporateAccount.sourcesCustom,
            products: corporateAccount.products,
            reasons: corporateAccount.reasons,
            sources: corporateAccount.sources,
        }
    }

    private static mapToMoney(amount: any): Money {
        if (isNullOrUndefined(amount)) {
            return undefined;
        }
        return {
            amount: parseInt(amount, 10),
            currency_code: "EUR"
        }
    }

    private static mapToAuthorisedSignatoryAddress(corporateAccount: CorporateAccount): Address {
        return {
            address: corporateAccount.authorisedSignatoryAddress,
            city: corporateAccount.authorisedSignatoryCity,
            countyOrRegion: corporateAccount.authorisedSignatoryCountyOrRegion,
            zipCode: corporateAccount.authorisedSignatoryZipCode,
            country: corporateAccount.authorisedSignatoryCountry
        }
    }

    public static isPhoneValid(phone: string): boolean {
        return phone == null || phone.length === 0 || isValidNumber(parse(phone));
    }

    public static isEmailValid(email: string): boolean {
        return email == null || email.length === 0 || NewCorporateDetailsComponent.EMAIL_REGEXP.test(email);
    }

    public static isUrlValid(url: string): boolean {
        return url == null || url.length === 0 || NewCorporateDetailsComponent.URL_REGEXP.test(url);
    }


    public static isAddressValid(address: Address): boolean {
        return isNullOrUndefined(address)
            || (address.address.trim().length === 0
                && isNullOrUndefined(address.country)
                && address.city.trim().length === 0
                && address.countyOrRegion.trim().length === 0
                && address.zipCode.trim().length === 0)
            || (address.address.trim().length >= 5
                && address.address.trim().length <= 150
                && !isNullOrUndefined(address.country)
                && address.city.trim().length >= 1
                && address.city.trim().length <= 50
                && address.zipCode.trim().length >= 1
                && address.zipCode.trim().length <= 50
                && !isNullOrUndefined(address.countyOrRegion)
                && address.countyOrRegion.trim().length >= 0
                && address.countyOrRegion.trim().length <= 50);
    }

    public static isAddressWithAttachmentsValid(address: Address): boolean {
        return isNullOrUndefined(address)
            || (address.address.trim().length === 0
                && isNullOrUndefined(address.country)
                && address.city.trim().length === 0
                && address.countyOrRegion.trim().length === 0
                && address.attachmentItems.length === 0
                && address.zipCode.trim().length === 0)
            || (address.address.trim().length >= 5
                && address.address.trim().length <= 150
                && !isNullOrUndefined(address.country)
                && address.city.trim().length >= 1
                && address.city.trim().length <= 50
                && address.zipCode.trim().length >= 1
                && address.zipCode.trim().length <= 50
                && address.attachmentItems.length >= 1
                && !isNullOrUndefined(address.countyOrRegion)
                && address.countyOrRegion.trim().length >= 0
                && address.countyOrRegion.trim().length <= 50);
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/modules/customers/+applications/new/detail/+details/corporate/new.corporate.details.component.ts